import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

// Các phần tử có thể nhận focus (đang hiển thị) bên trong dialog — cho bẫy Tab.
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

type DialogProps = {
  open: boolean
  onClose: () => void
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  // Dưới 768px trượt từ mép dưới, tràn ngang (dễ với tay khi cầm điện thoại 1 tay).
  sheetOnMobile?: boolean
  // Bỏ thanh tiêu đề khi nội dung đã có đầu riêng. title vẫn dùng làm aria-label.
  hideHeader?: boolean
}

const SIZE_CLASS = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
}

// Các hộp đang mở, mới nhất ở cuối. Hộp lồng nhau (xác nhận trong bảng khai): chỉ hộp trên cùng nhận phím,
// không thì Esc đóng cả 2 lớp.
const openDialogs: object[] = []

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  sheetOnMobile = false,
  hideHeader = false,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null)

  // onClose qua ref để effect dưới CHỈ phụ thuộc [open]. Nếu để onClose trong
  // deps: caller thường truyền function khai báo trong body (identity đổi mỗi
  // render) → gõ 1 ký tự trong form của dialog là effect chạy lại → cleanup trả
  // focus về nút mở dialog + setup focus panel → Ô INPUT MẤT FOCUS MỖI KÝ TỰ.
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const token = {}
    openDialogs.push(token)
    const panel = panelRef.current
    // Nếu autoFocus CHƯA đưa focus vào dialog → capture phần tử đang focus (để trả lại
    // khi đóng) rồi focus panel. Nếu ĐÃ có focus bên trong (autoFocus) → tôn trọng, bỏ
    // qua restore (không rõ trigger gốc — chấp nhận trả về body khi đóng).
    const alreadyInside = !!panel && panel.contains(document.activeElement)
    const previouslyFocused = alreadyInside ? null : (document.activeElement as HTMLElement | null)
    if (panel && !alreadyInside) panel.focus()

    const visibleFocusables = (): HTMLElement[] =>
      panel
        ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null)
        : []

    const onKey = (e: KeyboardEvent) => {
      if (openDialogs[openDialogs.length - 1] !== token) return
      if (e.key === 'Escape') {
        onCloseRef.current()
        return
      }
      if (e.key !== 'Tab' || !panel) return
      const els = visibleFocusables()
      if (els.length === 0) {
        e.preventDefault()
        panel.focus()
        return
      }
      const firstEl = els[0]
      const lastEl = els[els.length - 1]
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault()
        lastEl.focus()
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault()
        firstEl.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      openDialogs.splice(openDialogs.indexOf(token), 1)
      document.removeEventListener('keydown', onKey)
      // Còn hộp bên ngoài thì trang vẫn phải khoá cuộn.
      if (openDialogs.length === 0) document.body.style.overflow = ''
      previouslyFocused?.focus?.()
    }
  }, [open])

  if (!open) return null

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex justify-center',
        sheetOnMobile ? 'items-end md:items-center md:p-4' : 'items-center p-4',
      )}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="close"
        className="absolute inset-0 cursor-default bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          'relative z-10 w-full overflow-hidden border-border bg-surface shadow-elevated outline-none',
          sheetOnMobile
            ? 'animate-sheet-up rounded-t-2xl border-t md:max-w-md md:animate-none md:rounded-xl md:border'
            : cn('rounded-xl border', SIZE_CLASS[size]),
        )}
      >
        {hideHeader ? (
          <>
            {sheetOnMobile && <div aria-hidden className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-border-strong md:hidden" />}
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 z-10 rounded-md p-1.5 text-fg-subtle hover:bg-surface-muted hover:text-fg"
              aria-label="Đóng"
            >
              <X className="size-5" />
            </button>
          </>
        ) : (
          <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
            <div>
              <h2 className="text-sm font-semibold text-fg">{title}</h2>
              {description && (
                <p className="mt-1 text-xs text-fg-muted">{description}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-fg-subtle hover:bg-surface-muted hover:text-fg"
              aria-label="Đóng"
            >
              <X className="size-4" />
            </button>
          </div>
        )}

        <div className={cn('overflow-y-auto px-5 py-4', sheetOnMobile ? 'max-h-[90dvh]' : 'max-h-[70vh]')}>{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-border bg-surface-muted px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}
